<template>
  <span
    :class="[ns.b(), ns.is('circle', circle)]"
    :style="avatarStyle"
    data-test-name="avatar"
    :data-test-variant="[circle && 'circle'].join('-')"
    data-test-state=""
  >
    <img
      v-if="(src || srcSet) && !hasLoadError"
      :src="src"
      :alt="alt"
      :srcset="srcSet"
      :style="fitStyle"
      @error="handleError"
    />
    <b-icon v-else-if="icon" :class="ns.e('icon')">
      <component :is="icon" />
    </b-icon>
    <span v-else-if="name">{{ shortenName }}</span>
    <slot v-else>
      <b-icon :class="ns.e('icon')">
        <component :is="Profile" />
      </b-icon>
    </slot>
  </span>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { BIcon } from '@bigin/components/icon'
import { useNamespace } from '@bigin/hooks'
import { addUnit, isNumber } from '@bigin/utils'
import { Profile } from '@bigin/icons-vue'
import { avatarEmits, avatarProps } from './avatar'

import type { CSSProperties } from 'vue'

defineOptions({
  name: 'BAvatar',
})

const props = defineProps(avatarProps)
const emit = defineEmits(avatarEmits)

const ns = useNamespace('avatar')

const hasLoadError = ref(false)

const _getHashNumberFromString = (str: string) => {
  let hash = 1
  let i = 0

  if (!str) return 1

  do {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
    i++
  } while (i < str.length)

  // Return a number in range [0-7]
  return Math.abs(hash) % 8
}

const shortenName = computed(() => {
  if (props.name.length > 0) {
    const nameArr = props.name.split(' ')

    if (nameArr.length > 1) {
      return nameArr
        .slice(0, 2)
        .map((i) => i.charAt(0))
        .join('')
        .toUpperCase()
    }

    return props.name.slice(0, 2).toUpperCase()
  }

  return ''
})

const avatarStyle = computed(() => {
  const { size } = props
  const colors = [
    '#d90429',
    '#023047',
    '#219ebc',
    '#ffbe0b',
    '#fb5607',
    '#3a86ff',
    '#ff006e',
    '#ffbe0b',
  ]
  const gradients = [
    'linear-gradient(0.9deg, #ff5f6d 0%, #ffc371 100%)',
    'linear-gradient(155.94deg, #fbda61 15.44%, #ff8a1e 78.58%)',
    'linear-gradient(153.79deg, #ffd705 16.49%, #68c121 83.51%)',
    'linear-gradient(153.79deg, #a1ff8b 16.49%, #3f93ff 83.51%)',
    'linear-gradient(338.56deg, #9358f7 14.1%, #10d7e2 85.9%)',
    'linear-gradient(153.79deg, #7570ff 16.49%, #cf36ad 83.51%)',
    'linear-gradient(153.79deg, #ff56da 16.49%, #ff1b1b 83.51%)',
    'linear-gradient(180deg, #ff5959 0%, #ff7a00 100%)',
  ]
  const bgColor = `${
    colors[_getHashNumberFromString(props.colorId || props.name)]
  }`
  const bgGradient = `${
    gradients[_getHashNumberFromString(props.colorId || props.name)]
  }`
  return isNumber(size)
    ? (ns.cssVarBlock({
        size: addUnit(size) || '',
        'bg-color':
          (props.src || props.srcSet) && !hasLoadError.value
            ? 'transparent'
            : props.gradient
            ? bgGradient
            : bgColor,
      }) as CSSProperties)
    : undefined
})

const fitStyle = computed<CSSProperties>(() => ({
  objectFit: props.fit,
}))

// need reset hasLoadError to false if src changed
watch(
  () => props.src,
  () => (hasLoadError.value = false)
)

function handleError(e: Event) {
  hasLoadError.value = true
  emit('error', e)
}
</script>
